// Copyright 2025, University of Maryland, All Rights Reserved
import { workspaceBillsApi } from '@/api/workspace-bills-api'
import { generateBulkPrintHTML } from '@/utils/print-template'
import { renderBillDataToHTML } from '@/utils/bill-html-renderer'
import type { JSONSchema } from '@/api/types/schema'

/**
 * Handles bulk printing of legislative reviews for multiple bills
 */
export async function handleBulkPrint(
  bill_numbers: string[],
  session: string,
  schema?: JSONSchema
): Promise<void> {
  try {
    console.log('Starting bulk print for bills:', bill_numbers)
    console.log('Using schema:', schema?.title || 'No schema provided')
    
    // Fetch all AI reviews for the selected bills
    const review_promises = bill_numbers.map(bill_number => 
      workspaceBillsApi.getAIReview(session, bill_number)
    )
    
    const reviews = await Promise.all(review_promises)
    console.log('Fetched reviews for bills:', reviews.map(r => r.bill_number || 'unknown'))
    
    // Generate HTML content for each bill using the shared renderer
    const bill_contents = reviews.map((review_data, index) => {
      const bill_number = bill_numbers[index]
      const title = `AI Legislative Review: ${bill_number}`
      
      console.log(`Processing bill ${bill_number} data:`, Object.keys(review_data || {}))
      
      // Render the bill data to HTML using shared utility
      const content = renderBillDataToHTML(review_data, schema)
      
      return {
        title,
        content
      }
    })
    
    // Generate the bulk print HTML
    const bulk_print_html = generateBulkPrintHTML(bill_contents)
    
    // Open print window
    const print_window = window.open('', '_blank', `width=${screen.width},height=${screen.height},fullscreen=yes,scrollbars=yes`)
    
    if (!print_window) {
      throw new Error('Could not open print window')
    }
    
    // Write the HTML and initiate printing
    print_window.document.write(bulk_print_html)
    print_window.document.close()
    
    // Wait for content to load then print
    print_window.onload = () => {
      print_window.focus()
      print_window.print()
      print_window.close()
    }
    
    console.log('Bulk print initiated successfully')
    
  } catch (error) {
    console.error('Error in bulk print:', error)
    throw error
  }
}